"use client";

import { type ReactNode, useEffect, useMemo } from "react";
import { type Cache, SWRConfig } from "swr";
import { clientFetch } from "@/lib";

/**
 * Custom SWR cache provider that persists cache to localStorage
 * This enables cache persistence across page reloads
 */
function useLocalStorageProvider<T = unknown>(): () => Cache<T> {
	const map = useMemo(
		() =>
			new Map<string, T>(
				JSON.parse(
					(typeof window !== "undefined" &&
						localStorage.getItem("pos-app-cache")) ||
						"[]",
				) as [string, T][],
			),
		[],
	);

	useEffect(() => {
		const handleBeforeUnload = () => {
			const appCache = JSON.stringify(Array.from(map.entries()));
			typeof window !== "undefined" &&
				localStorage?.setItem("pos-app-cache", appCache);
		};

		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => window.removeEventListener("beforeunload", handleBeforeUnload);
	}, [map]);

	return () => map as Cache<T>;
}

interface SWRProviderProps {
	children: ReactNode;
}

/**
 * SWR Configuration Provider
 *
 * Features:
 * - Sets clientFetch as the default fetcher for all SWR hooks
 * - Enables cache persistence via localStorage
 * - Disables automatic retry on errors (errors are handled by clientFetch)
 * - Provides optimistic UI updates and revalidation
 *
 * Usage:
 * Wrap your app with this provider to enable SWR with automatic clientFetch integration
 *
 * @example
 * ```tsx
 * // No need to pass fetcher to useSWR anymore!
 * const { data } = useSWR("/api/products");
 * // Instead of: useSWR("/api/products", clientFetch)
 * ```
 */
export function SWRProvider({ children }: SWRProviderProps) {
	const localStorageProvider = useLocalStorageProvider();

	return (
		<SWRConfig
			value={{
				// Enable localStorage cache persistence (client-side only)
				...(typeof window !== "undefined" && {
					provider: localStorageProvider,
				}),

				// Set clientFetch as default fetcher
				// All useSWR hooks will use clientFetch by default
				fetcher: clientFetch,

				// clientFetch already toasts errors; retry only transient ones.
				shouldRetryOnError: true,
				errorRetryCount: 3,
				errorRetryInterval: 5000,
				onErrorRetry: (error, _key, _config, revalidate, { retryCount }) => {
					// 401 is handled by the refresh flow, 403 by the CSRF guard —
					// retrying either just multiplies the toast.
					const status = (error as { response?: { status?: number } })?.response
						?.status;
					if (status === 401 || status === 403) return;
					if (error?.name === "TimeoutError") return;
					if (retryCount >= 3) return;
					setTimeout(() => revalidate({ retryCount }), 5000);
				},
				revalidateOnFocus: true,
				revalidateOnReconnect: true,
				dedupingInterval: 2000,
			}}
		>
			{children}
		</SWRConfig>
	);
}
